import { useEffect, useRef, useState } from 'react';
import {
  BarChart3,
  Boxes,
  GitBranch,
  HelpCircle,
  KeyRound,
  MessageSquare,
  Moon,
  Search,
  Sun,
} from 'lucide-react';
import { clearToken, getToken, TOKEN_CHANGE_EVENT } from './lib/api';
import { useShortcuts } from './lib/hooks';
import { applyTheme, readTheme, type Theme } from './lib/theme';
import { CallPathPage } from './pages/CallPathPage';
import { DashboardPage } from './pages/DashboardPage';
import { SearchPage } from './pages/SearchPage';
import { StructuralPage } from './pages/StructuralPage';
import { TokenSetup } from './components/TokenSetup';
import { ShortcutsHelp } from './components/ShortcutsHelp';
import { FeedbackModal } from './components/FeedbackModal';
import { Toaster } from './components/Toaster';

type Page = 'search' | 'structural' | 'call_path' | 'dashboard';

export default function App() {
  const [token, setTokenState] = useState<string | null>(() => getToken());
  const [page, setPage] = useState<Page>('search');
  const [theme, setTheme] = useState<Theme>(() => readTheme());
  const [helpOpen, setHelpOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [tokenSetupMessage, setTokenSetupMessage] = useState<string | null>(null);

  // Listen for "the API just told us 401" events. The api module clears the token from
  // localStorage when that happens; this brings the UI back to the setup screen so the user
  // can paste a fresh one. `storage` covers other-tab changes; TOKEN_CHANGE_EVENT (dispatched
  // by setToken/clearToken) covers same-tab changes — the `storage` event never fires in the
  // tab that made the change.
  // `tokenRef` mirrors the token state so `sync` can detect a real change without
  // depending on the (stale, `[]`-deps) closure value — and the setState updater
  // stays pure (the message side effect runs outside it). Every token mutation
  // routes through setToken/clearToken (→ TOKEN_CHANGE_EVENT) or `storage`, so the
  // ref only ever needs updating here.
  const tokenRef = useRef(token);
  useEffect(() => {
    const sync = () => {
      const current = getToken();
      if (current === tokenRef.current) return;
      tokenRef.current = current;
      setTokenState(current);
      if (!current) {
        setTokenSetupMessage('Server rejected the token — please paste a new one.');
      }
    };
    window.addEventListener('storage', sync);
    window.addEventListener(TOKEN_CHANGE_EVENT, sync);
    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener(TOKEN_CHANGE_EVENT, sync);
    };
  }, []);

  useShortcuts({
    '?': {
      handler: () => setHelpOpen(true),
    },
    Escape: {
      handler: () => {
        setHelpOpen(false);
        setFeedbackOpen(false);
      },
    },
  });

  const onToggleTheme = () => {
    const next: Theme = theme === 'light' ? 'dark' : 'light';
    setTheme(next);
    applyTheme(next);
  };

  if (!token) {
    return (
      <>
        <TokenSetup
          onDone={() => {
            setTokenState(getToken());
            setTokenSetupMessage(null);
          }}
          message={tokenSetupMessage ?? undefined}
        />
        <Toaster />
      </>
    );
  }

  return (
    <div className="h-screen flex flex-col">
      <header className="flex items-center gap-2 border-b border-zinc-200 dark:border-zinc-800 px-4 py-2 bg-white dark:bg-zinc-900">
        <span className="text-base font-semibold">rag-a-muffin</span>
        <span className="hidden sm:inline text-xs font-mono text-zinc-500 dark:text-zinc-400">
          code-RAG GUI
        </span>
        <nav className="ml-4 flex rounded border border-zinc-300 dark:border-zinc-700 overflow-hidden text-sm">
          <button
            onClick={() => setPage('search')}
            className={`flex items-center gap-1 px-3 py-1 ${page === 'search' ? 'bg-indigo-600 text-white' : 'bg-transparent'}`}
          >
            <Search size={14} /> Search
          </button>
          <button
            onClick={() => setPage('structural')}
            className={`flex items-center gap-1 px-3 py-1 ${page === 'structural' ? 'bg-violet-600 text-white' : 'bg-transparent'}`}
          >
            <Boxes size={14} /> Structural
          </button>
          <button
            onClick={() => setPage('call_path')}
            className={`flex items-center gap-1 px-3 py-1 ${page === 'call_path' ? 'bg-emerald-600 text-white' : 'bg-transparent'}`}
          >
            <GitBranch size={14} /> Path
          </button>
          <button
            onClick={() => setPage('dashboard')}
            className={`flex items-center gap-1 px-3 py-1 ${page === 'dashboard' ? 'bg-amber-600 text-white' : 'bg-transparent'}`}
          >
            <BarChart3 size={14} /> Dashboard
          </button>
        </nav>
        <div className="ml-auto flex items-center gap-2">
          <span className="hidden md:flex items-center gap-1 text-xs font-mono text-zinc-500 dark:text-zinc-400 px-2 py-1 rounded bg-zinc-100 dark:bg-zinc-800">
            <KeyRound size={12} /> token: {token.slice(0, 4)}…{token.slice(-2)}
          </span>
          <button
            type="button"
            onClick={() => {
              clearToken();
              setTokenState(null);
            }}
            className="text-xs px-2 py-1 rounded hover:bg-zinc-100 dark:hover:bg-zinc-800"
            title="forget token"
          >
            forget
          </button>
          <button
            type="button"
            onClick={() => setFeedbackOpen(true)}
            className="flex items-center gap-1 text-xs px-2 py-1 rounded hover:bg-zinc-100 dark:hover:bg-zinc-800"
          >
            <MessageSquare size={14} /> feedback
          </button>
          <button
            type="button"
            onClick={onToggleTheme}
            className="text-xs px-2 py-1 rounded hover:bg-zinc-100 dark:hover:bg-zinc-800"
            title="toggle theme"
          >
            {theme === 'dark' ? <Sun size={14} /> : <Moon size={14} />}
          </button>
          <button
            type="button"
            onClick={() => setHelpOpen(true)}
            className="text-xs px-2 py-1 rounded hover:bg-zinc-100 dark:hover:bg-zinc-800"
            title="shortcuts (?)"
          >
            <HelpCircle size={14} />
          </button>
        </div>
      </header>

      <div className="flex-1 min-h-0 flex">
        {page === 'search' ? (
          <SearchPage theme={theme} />
        ) : page === 'structural' ? (
          <StructuralPage theme={theme} />
        ) : page === 'call_path' ? (
          <CallPathPage theme={theme} />
        ) : (
          <DashboardPage theme={theme} />
        )}
      </div>

      {helpOpen && <ShortcutsHelp onClose={() => setHelpOpen(false)} />}
      {feedbackOpen && <FeedbackModal onClose={() => setFeedbackOpen(false)} />}
      <Toaster />
    </div>
  );
}
